import { useEffect, useRef, useState } from "react";
import {
  ArrowLeft, Gamepad2, Monitor, X, Maximize2, Minimize2, ExternalLink, Play, GripVertical,
  Target, Pickaxe,
} from "lucide-react";
import BallSnipe from "@/components/games/BallSnipe";
import MistMiner from "@/components/games/MistMiner";
import SpaceApp from "@/game/space/SpaceApp";

// STAGE 6 · PHASE 3 — Decoupled PiP Games (100% client-side, $0.00 server footprint).
// External consoles + creator HTML5 games run inside the browser only; NO backend,
// NO database, NO server loops.

const CONSOLES = [
  { id: "xbox", title: "Xbox Cloud", url: "https://www.xbox.com/play", color: "#107C10", tag: "Cloud Gaming" },
  { id: "psn", title: "PlayStation", url: "https://www.playstation.com", color: "#0070D1", tag: "PSN Profile" },
  { id: "steam", title: "Steam Web", url: "https://store.steampowered.com", color: "#66C0F4", tag: "Library" },
];

// Self-contained HTML5 mini-games (srcDoc) authored for the marketplace — they run
// entirely on the device engine, sandboxed, with zero external calls.
const REFLEX = "<!doctype html><html><head><meta name=viewport content='width=device-width,initial-scale=1'><style>html,body{margin:0;height:100%;background:#0a0a12;overflow:hidden;font-family:monospace;color:#fff;touch-action:manipulation}#t{position:absolute;width:52px;height:52px;border-radius:50%;background:#a78bfa;box-shadow:0 0 20px #a78bfa}#s{position:absolute;top:8px;left:10px;font-size:14px}</style></head><body><div id=s>Score: 0</div><div id=t></div><script>var t=document.getElementById('t'),s=document.getElementById('s'),sc=0;function p(){t.style.left=(Math.random()*(innerWidth-52))+'px';t.style.top=(30+Math.random()*(innerHeight-82))+'px';}t.addEventListener('pointerdown',function(e){e.preventDefault();sc++;s.textContent='Score: '+sc;p();});p();</script></body></html>";
const PONG = "<!doctype html><html><head><meta name=viewport content='width=device-width,initial-scale=1'><style>html,body{margin:0;height:100%;background:#05060a;overflow:hidden}canvas{display:block}</style></head><body><canvas id=c></canvas><script>var c=document.getElementById('c'),x=c.getContext('2d');function rs(){c.width=innerWidth;c.height=innerHeight;}rs();onresize=rs;var pw=70,ph=10,px=c.width/2-35,bx=c.width/2,by=40,vx=3,vy=3,r=8,sc=0,go=false;function mv(e){var t=e.touches?e.touches[0]:e;px=t.clientX-35;}c.addEventListener('pointermove',mv);c.addEventListener('touchmove',function(e){e.preventDefault();mv(e);},{passive:false});c.addEventListener('pointerdown',function(){if(go){go=false;bx=c.width/2;by=40;vx=3;vy=3;sc=0;}});function loop(){x.fillStyle='#05060a';x.fillRect(0,0,c.width,c.height);if(!go){bx+=vx;by+=vy;if(bx<r||bx>c.width-r)vx=-vx;if(by<r)vy=-vy;var py=c.height-30;if(by>py-r&&by<py+ph&&bx>px&&bx<px+pw){vy=-Math.abs(vy);sc++;}if(by>c.height)go=true;}x.fillStyle='#00f5d4';x.fillRect(px,c.height-30,pw,ph);x.beginPath();x.arc(bx,by,r,0,7);x.fillStyle='#a78bfa';x.fill();x.fillStyle='#fff';x.font='14px monospace';x.fillText(go?'Game Over — tap · '+sc:'Score '+sc,10,20);requestAnimationFrame(loop);}loop();</script></body></html>";
const POP = "<!doctype html><html><head><meta name=viewport content='width=device-width,initial-scale=1'><style>html,body{margin:0;height:100%;background:#0b0416;overflow:hidden;font-family:monospace}canvas{display:block}</style></head><body><canvas id=c></canvas><script>var c=document.getElementById('c'),x=c.getContext('2d');function rs(){c.width=innerWidth;c.height=innerHeight;}rs();onresize=rs;var b=[],sc=0;function sp(){b.push({x:20+Math.random()*(c.width-40),y:c.height+20,r:13+Math.random()*13,s:1+Math.random()*2,h:Math.random()*360});}setInterval(sp,700);c.addEventListener('pointerdown',function(e){var t=e.touches?e.touches[0]:e,mx=t.clientX,my=t.clientY;for(var i=b.length-1;i>=0;i--){if(Math.hypot(b[i].x-mx,b[i].y-my)<b[i].r){b.splice(i,1);sc++;break;}}});function loop(){x.fillStyle='#0b0416';x.fillRect(0,0,c.width,c.height);for(var i=b.length-1;i>=0;i--){var o=b[i];o.y-=o.s;if(o.y<-30)b.splice(i,1);x.beginPath();x.arc(o.x,o.y,o.r,0,7);x.fillStyle='hsl('+o.h+',90%,60%)';x.fill();}x.fillStyle='#fff';x.font='14px monospace';x.fillText('Popped: '+sc,10,20);requestAnimationFrame(loop);}loop();</script></body></html>";

const GAMES = [
  { id: "reflex", title: "Reflex Orb", author: "@nova", tag: "Arcade", color: "#A78BFA", srcDoc: REFLEX },
  { id: "pong", title: "Neon Pong", author: "@jett", tag: "Classic", color: "#00F5D4", srcDoc: PONG },
  { id: "pop", title: "Bubble Pop", author: "@mira", tag: "Casual", color: "#F472B6", srcDoc: POP },
];

function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }

export default function PipGamesZone({ onBack, initialOriginal = null }) {
  const [original, setOriginal] = useState(initialOriginal);   // 'snipe' | 'miner' | null
  const [tab, setTab] = useState("console");
  const [pip, setPip] = useState(null); // {title, url?, srcDoc?}
  const [pos, setPos] = useState({ x: 16, y: 96 });
  const [big, setBig] = useState(false);
  const containerRef = useRef(null);
  const dragRef = useRef(null);

  const launch = (item) => { setPip(item); setBig(false); setPos({ x: 16, y: 96 }); };

  // Draggable PiP window (pointer-based; works for touch + mouse).
  useEffect(() => {
    if (!pip) return;
    const onMove = (e) => {
      if (!dragRef.current) return;
      const c = containerRef.current?.getBoundingClientRect();
      const w = big ? (c ? c.width * 0.92 : 320) : 244;
      const h = big ? (c ? c.height * 0.7 : 360) : 300;
      const nx = clamp(dragRef.current.ox + (e.clientX - dragRef.current.sx), 0, (c ? c.width : 412) - w);
      const ny = clamp(dragRef.current.oy + (e.clientY - dragRef.current.sy), 0, (c ? c.height : 800) - 44);
      setPos({ x: nx, y: ny });
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => { window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); };
  }, [pip, big]);

  const startDrag = (e) => {
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
  };

  const winW = big ? "92%" : "244px";
  const winH = big ? "70%" : "300px";

  if (original === "snipe") return <BallSnipe onBack={() => setOriginal(null)} />;
  if (original === "miner") return <MistMiner onBack={() => setOriginal(null)} />;
  if (original === "space") return <SpaceApp onBack={() => setOriginal(null)} />;

  return (
    <div ref={containerRef} className="absolute inset-0 z-30 flex flex-col overflow-hidden bg-slate-950" data-testid="pip-games-zone">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
        <button onClick={onBack} data-testid="pip-games-back" className="grid h-9 w-9 place-items-center rounded-xl border-2 border-white/15 bg-white/5 active:scale-95">
          <ArrowLeft size={16} className="text-white" />
        </button>
        <div className="leading-tight">
          <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-indigo-300">Mist Play · Games</p>
          <h2 className="font-serif text-xl text-white">Play Zone</h2>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 px-3 py-2.5" data-testid="pip-tabs">
        <button onClick={() => setTab("console")} data-testid="pip-tab-console"
          className={["flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 font-mono text-[10px] font-black uppercase tracking-wide", tab === "console" ? "border-white bg-white text-slate-950" : "border-white/15 bg-white/[0.03] text-white/60"].join(" ")}>
          <Monitor size={13} /> My Game Accounts
        </button>
        <button onClick={() => setTab("market")} data-testid="pip-tab-market"
          className={["flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 font-mono text-[10px] font-black uppercase tracking-wide", tab === "market" ? "border-white bg-white text-slate-950" : "border-white/15 bg-white/[0.03] text-white/60"].join(" ")}>
          <Gamepad2 size={13} /> Instant Games
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto no-scrollbar p-4">
        {/* Mist Originals — our own games (shared gem economy + rewards) */}
        <p className="mb-2 font-mono text-[8px] uppercase tracking-[0.24em] text-white/40">Mist Originals</p>

        {/* Featured — MiST: The Rise of Civilisation (4X strategy-RPG) */}
        <button onClick={() => setOriginal("space")} data-testid="original-space-supremacy"
          className="relative mb-2.5 flex h-[150px] w-full items-end overflow-hidden rounded-2xl border-2 border-black shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/gallery/media/9977354a862d`} alt="MiST: The Rise of Civilisation" className="absolute inset-0 h-full w-full object-cover" />
          <span className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
          <span className="relative z-10 flex w-full items-center justify-between p-3">
            <span className="text-left">
              <span className="block font-mono text-[7px] uppercase tracking-[0.24em] text-cyan-300/90">The Rise of Civilisation</span>
              <span className="block font-serif text-xl leading-none text-white">MiST</span>
            </span>
            <span className="grid h-9 w-9 place-items-center rounded-full border-2 border-cyan-300 bg-cyan-300/15">
              <Play size={16} className="text-cyan-200" />
            </span>
          </span>
        </button>

        {/* Desktop — launch MiST in its own dedicated, locked game window. */}
        <button
          data-testid="space-open-window"
          onClick={() => window.open(`${window.location.origin}/play/space`, "mist_game", "popup,width=480,height=854,noopener")}
          className="mb-2.5 hidden w-full items-center justify-center gap-1.5 rounded-xl border-2 border-cyan-400/40 bg-cyan-400/5 px-3 py-2 font-mono text-[9px] font-black uppercase tracking-[0.18em] text-cyan-200/90 active:scale-[0.99] md:flex"
        >
          <ExternalLink size={12} /> Open in dedicated game window
        </button>

        <div className="mb-4 grid grid-cols-2 gap-2.5" data-testid="mist-originals">
          <button onClick={() => setOriginal("snipe")} data-testid="original-ball-snipe"
            className="flex flex-col justify-between rounded-2xl border-2 border-black bg-[#a78bfa] p-3 text-left shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none min-h-[110px]">
            <Target size={22} className="text-black" />
            <span><span className="block font-serif text-lg leading-none text-black">Ball Snipe</span><span className="mt-1 block font-mono text-[7px] uppercase tracking-[0.18em] text-black/70">Bounce · smash · gems</span></span>
          </button>
          <button onClick={() => setOriginal("miner")} data-testid="original-mist-miner"
            className="flex flex-col justify-between rounded-2xl border-2 border-black bg-[#f59e0b] p-3 text-left shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none min-h-[110px]">
            <Pickaxe size={22} className="text-black" />
            <span><span className="block font-serif text-lg leading-none text-black">Tetminer</span><span className="mt-1 block font-mono text-[7px] uppercase tracking-[0.18em] text-black/70">Dig · match icons · gems</span></span>
          </button>
        </div>
        {tab === "console" ? (
          <div className="space-y-2.5" data-testid="console-gateway">
            <p className="font-mono text-[9px] leading-relaxed text-white/40">Open your game accounts. Free to play — nothing to download.</p>
            {CONSOLES.map((c) => (
              <div key={c.id} data-testid={`console-card-${c.id}`} className="flex items-center gap-3 rounded-2xl border-2 border-white/12 bg-white/[0.04] p-3.5">
                <span className="grid h-11 w-11 place-items-center rounded-xl border-2" style={{ borderColor: c.color }}>
                  <Monitor size={20} style={{ color: c.color }} />
                </span>
                <div className="flex-1 leading-tight min-w-0">
                  <p className="font-sans text-[15px] font-bold text-white truncate">{c.title}</p>
                  <p className="font-mono text-[8.5px] uppercase tracking-wide text-white/45">{c.tag}</p>
                </div>
                {/* PRIMARY — open in a fresh browser tab (always works) */}
                <a href={c.url} target="_blank" rel="noopener noreferrer" data-testid={`console-newtab-${c.id}`}
                  className="shrink-0 flex items-center gap-1.5 rounded-lg border-2 border-white bg-white px-3 py-2 font-mono text-[10px] font-black uppercase tracking-wide text-slate-950 active:translate-y-[1px]" aria-label="Open in your browser">
                  <ExternalLink size={14} /> Open
                </a>
                {/* SECONDARY — small floating window (may be blocked by some providers) */}
                <button onClick={() => launch(c)} data-testid={`console-launch-${c.id}`} className="shrink-0 grid h-9 w-9 place-items-center rounded-lg border-2 border-white/20 bg-white/[0.06] active:scale-95" aria-label="Open in floating window">
                  <Play size={15} className="text-white/70" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="game-marketplace">
            <p className="font-mono text-[9px] leading-relaxed text-white/40">Lightweight games by platform creators. Free to play — nothing to download.</p>
            {GAMES.map((g) => (
              <button key={g.id} onClick={() => launch(g)} data-testid={`game-card-${g.id}`} className="flex w-full items-center gap-3 rounded-2xl border-2 border-white/12 bg-white/[0.04] p-3.5 text-left active:translate-x-[2px] active:translate-y-[2px]">
                <span className="grid h-11 w-11 place-items-center rounded-xl border-2" style={{ borderColor: g.color }}>
                  <Gamepad2 size={20} style={{ color: g.color }} />
                </span>
                <div className="flex-1 leading-tight">
                  <p className="font-sans text-[15px] font-bold text-white">{g.title}</p>
                  <p className="font-mono text-[8.5px] uppercase tracking-wide text-white/45">{g.tag} · {g.author}</p>
                </div>
                <Play size={16} className="text-white/60" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Draggable PiP player window */}
      {pip && (
        <div
          data-testid="pip-window"
          className="absolute overflow-hidden rounded-2xl border-2 border-indigo-400 bg-slate-900 shadow-[0_10px_40px_rgba(0,0,0,0.6)]"
          style={{ left: pos.x, top: pos.y, width: winW, height: winH, zIndex: 50 }}
        >
          <div
            data-testid="pip-window-drag"
            onPointerDown={startDrag}
            className="flex cursor-grab items-center gap-1.5 border-b border-white/10 bg-slate-800 px-2 py-1.5 active:cursor-grabbing"
            style={{ touchAction: "none" }}
          >
            <GripVertical size={13} className="text-white/40" />
            <span className="flex-1 truncate font-mono text-[10px] font-black uppercase tracking-wide text-white">{pip.title}</span>
            <button onClick={() => setBig((b) => !b)} data-testid="pip-window-resize" className="grid h-6 w-6 place-items-center rounded text-white/70 active:scale-95">
              {big ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
            </button>
            <button onClick={() => setPip(null)} data-testid="pip-window-close" className="grid h-6 w-6 place-items-center rounded text-white/70 active:scale-95">
              <X size={14} />
            </button>
          </div>
          <div className="relative h-[calc(100%-30px)] w-full bg-black">
            {pip.srcDoc ? (
              <iframe title={pip.title} srcDoc={pip.srcDoc} sandbox="allow-scripts allow-pointer-lock" className="h-full w-full border-0" data-testid="pip-iframe-game" />
            ) : (
              <>
                <iframe title={pip.title} src={pip.url} className="h-full w-full border-0" data-testid="pip-iframe-console" />
                <a href={pip.url} target="_blank" rel="noopener noreferrer" className="absolute bottom-1.5 right-1.5 flex items-center gap-1 rounded-full border border-white/20 bg-black/70 px-2 py-1 font-mono text-[8px] uppercase tracking-wide text-white/80" data-testid="pip-open-newtab">
                  <ExternalLink size={10} /> New tab
                </a>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
